import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, Settings, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import GameGrid from "../components/GameGrid";
import Keyboard from "../components/Keyboard";
import InstructionsModal from "../components/InstructionsModal";
import SettingsModal from "../components/SettingsModal";
import ResultModal from "../components/ResultModal";
import ClubModal from "../components/ClubModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_ATTEMPTS = 6;

const GamePage = () => {
  // Game state
  const [movieData, setMovieData] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [feedbacks, setFeedbacks] = useState([]);
  const [gameStatus, setGameStatus] = useState("playing"); // playing, won, lost
  const [isLoading, setIsLoading] = useState(true);
  const [keyStates, setKeyStates] = useState({});
  const [showHint, setShowHint] = useState(false);
  const [hint, setHint] = useState("");
  
  // Modal states
  const [showInstructions, setShowInstructions] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showClub, setShowClub] = useState(false);
  const [revealData, setRevealData] = useState(null);
  
  // Settings
  const [hardMode, setHardMode] = useState(() => {
    const saved = localStorage.getItem("guessthebolly_hardmode");
    return saved === "true";
  });
  
  // User data
  const [streak, setStreak] = useState(() => {
    const saved = localStorage.getItem("guessthebolly_streak");
    return saved ? parseInt(saved, 10) : 0;
  });
  const [club, setClub] = useState(() => {
    return localStorage.getItem("guessthebolly_club") || null;
  });
  
  // Animation states
  const [shakeRow, setShakeRow] = useState(-1);
  const [bounceRow, setBounceRow] = useState(-1);
  
  const gameContainerRef = useRef(null);

  // Fetch daily movie on mount
  useEffect(() => {
    fetchDailyMovie();
    
    // Check if first visit
    const hasVisited = localStorage.getItem("guessthebolly_visited");
    if (!hasVisited) {
      setShowInstructions(true);
      localStorage.setItem("guessthebolly_visited", "true");
    }
  }, []);

  // Load saved game state
  useEffect(() => {
    if (movieData) {
      const savedState = localStorage.getItem(`guessthebolly_game_${movieData.day}`);
      if (savedState) {
        const state = JSON.parse(savedState);
        setGuesses(state.guesses || []);
        setFeedbacks(state.feedbacks || []);
        setGameStatus(state.gameStatus || "playing");
        setKeyStates(state.keyStates || {});
        
        if (state.gameStatus === "won" || state.gameStatus === "lost") {
          fetchRevealData(movieData.day);
        }
      }
    }
  }, [movieData]);

  // Save game state
  useEffect(() => {
    if (movieData && (guesses.length > 0 || gameStatus !== "playing")) {
      const state = {
        guesses,
        feedbacks,
        gameStatus,
        keyStates,
      };
      localStorage.setItem(`guessthebolly_game_${movieData.day}`, JSON.stringify(state));
    }
  }, [guesses, feedbacks, gameStatus, keyStates, movieData]);

  // Save settings
  useEffect(() => {
    localStorage.setItem("guessthebolly_hardmode", hardMode.toString());
  }, [hardMode]);

  // Save streak
  useEffect(() => {
    localStorage.setItem("guessthebolly_streak", streak.toString());
  }, [streak]);

  // Save club
  useEffect(() => {
    if (club) {
      localStorage.setItem("guessthebolly_club", club);
    }
  }, [club]);

  const fetchDailyMovie = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/daily-movie`);
      setMovieData(response.data);
    } catch (error) {
      console.error("Failed to fetch daily movie:", error);
      toast.error("Failed to load today's movie. Please refresh.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRevealData = async (day) => {
    try {
      const response = await axios.get(`${API}/movie-reveal/${day}`);
      setRevealData(response.data);
    } catch (error) {
      console.error("Failed to fetch reveal data:", error);
    }
  };

  const fetchHint = async () => {
    if (!movieData) return;
    try {
      const response = await axios.get(`${API}/hint/${movieData.day}`);
      setHint(response.data.hint);
      setShowHint(true);
    } catch (error) {
      console.error("Failed to fetch hint:", error);
      toast.error("Failed to get hint");
    }
  };

  const submitGuess = useCallback(async () => {
    if (!movieData || gameStatus !== "playing") return;
    
    // Add spaces at correct positions
    let formattedGuess = currentGuess.toUpperCase();
    if (movieData.space_positions.length > 0) {
      let chars = formattedGuess.split("");
      movieData.space_positions.forEach((pos) => {
        if (chars.length >= pos) {
          chars.splice(pos, 0, " ");
        }
      });
      formattedGuess = chars.join("");
    }
    
    if (formattedGuess.replace(/\s/g, "").length !== movieData.title_length - movieData.space_positions.length) {
      setShakeRow(guesses.length);
      setTimeout(() => setShakeRow(-1), 500);
      toast.error(`Enter all ${movieData.title_length - movieData.space_positions.length} letters`);
      return;
    }

    try {
      const response = await axios.post(`${API}/check-guess`, {
        guess: formattedGuess,
        day: movieData.day,
      });

      const { is_correct, feedback } = response.data;
      
      // Add the guess and feedback to state
      const newGuesses = [...guesses, formattedGuess];
      const newFeedbacks = [...feedbacks, feedback];
      
      setGuesses(newGuesses);
      setFeedbacks(newFeedbacks);
      setCurrentGuess("");
      
      // Update key states
      const newKeyStates = { ...keyStates };
      formattedGuess.split("").forEach((letter, index) => {
        if (letter === " ") return;
        const currentState = newKeyStates[letter];
        const newState = feedback[index];
        if (newState === "correct") {
          newKeyStates[letter] = "correct";
        } else if (newState === "present" && currentState !== "correct") {
          newKeyStates[letter] = "present";
        } else if (newState === "absent" && !currentState) {
          newKeyStates[letter] = "absent";
        }
      });
      setKeyStates(newKeyStates);
      
      // Wait for color animation to complete
      const animationDuration = 300 + (movieData.title_length * 100);
      
      setTimeout(() => {
        if (is_correct) {
          setBounceRow(newGuesses.length - 1);
          setTimeout(() => setBounceRow(-1), 600);
          
          setStreak(prev => prev + 1);
          setGameStatus("won");
          
          fetchRevealData(movieData.day);
          
          // Show club selection on first win
          const savedClub = localStorage.getItem("guessthebolly_club");
          if (!savedClub) {
            setTimeout(() => setShowClub(true), 800);
          } else {
            setTimeout(() => setShowResult(true), 800);
          }
          
          // Save stats
          axios.post(`${API}/stats`, {
            day: movieData.day,
            won: true,
            attempts: newGuesses.length,
            club: localStorage.getItem("guessthebolly_club"),
          }).catch(console.error);
          
        } else if (newGuesses.length >= MAX_ATTEMPTS) {
          setStreak(0);
          setGameStatus("lost");
          
          fetchRevealData(movieData.day);
          setTimeout(() => setShowResult(true), 800);
          
          // Save stats
          axios.post(`${API}/stats`, {
            day: movieData.day,
            won: false,
            attempts: newGuesses.length,
            club: localStorage.getItem("guessthebolly_club"),
          }).catch(console.error);
        }
      }, animationDuration);
      
    } catch (error) {
      console.error("Failed to check guess:", error);
      setShakeRow(guesses.length);
      setTimeout(() => setShakeRow(-1), 500);
      toast.error(error.response?.data?.detail || "Failed to check guess");
    }
  }, [movieData, gameStatus, currentGuess, guesses, feedbacks, keyStates]);

  const handleKeyPress = useCallback((key) => {
    if (gameStatus !== "playing" || !movieData) return;

    if (key === "ENTER") {
      // Will be handled by submitGuessRef
      return "SUBMIT";
    } else if (key === "BACKSPACE") {
      setCurrentGuess((prev) => prev.slice(0, -1));
    } else if (key.length === 1 && /[A-Z]/.test(key)) {
      const maxLength = movieData.title_length - movieData.space_positions.length;
      if (currentGuess.length < maxLength) {
        setCurrentGuess((prev) => prev + key);
      }
    }
    return null;
  }, [currentGuess, gameStatus, movieData]);

  // Wrapper for keyboard to handle submit
  const onKeyboardPress = (key) => {
    const result = handleKeyPress(key);
    if (result === "SUBMIT") {
      submitGuess();
    }
  };

  // Physical keyboard handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showInstructions || showSettings || showResult || showClub) return;
      
      if (e.key === "Enter") {
        e.preventDefault();
        submitGuess();
      } else if (e.key === "Backspace") {
        handleKeyPress("BACKSPACE");
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        handleKeyPress(e.key.toUpperCase());
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyPress, showInstructions, showSettings, showResult, showClub, submitGuess]);

  const handleClubSelect = (selectedClub) => {
    setClub(selectedClub);
    setShowClub(false);
    setTimeout(() => setShowResult(true), 300);
  };

  // Show hint button after attempt 4 (index 4 = 5th attempt)
  const canShowHintButton = guesses.length >= 4 && gameStatus === "playing" && !showHint;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary font-body">Loading today's movie...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      ref={gameContainerRef}
      className="min-h-screen bg-dark flex flex-col"
      data-testid="game-page"
    >
      {/* Header */}
      <header className="w-full border-b border-white/10 bg-dark-paper/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setShowInstructions(true)}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            data-testid="help-button"
            aria-label="How to play"
          >
            <HelpCircle className="w-6 h-6 text-text-secondary" />
          </button>
          
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold text-gold-gradient" data-testid="game-title">
              GuessTheBolly
            </h1>
            <p className="text-xs text-text-muted font-body">
              Day {movieData?.day} • {movieData?.difficulty}
            </p>
          </div>
          
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            data-testid="settings-button"
            aria-label="Settings"
          >
            <Settings className="w-6 h-6 text-text-secondary" />
          </button>
        </div>
      </header>

      {/* Main Game Area */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-6">
        <div className="w-full max-w-lg">
          {/* Streak Display */}
          <div className="flex justify-center mb-6">
            <div className="glass px-4 py-2 rounded-full flex items-center gap-2">
              <span className="text-gold text-lg">🔥</span>
              <span className="text-text-primary font-body font-semibold">{streak}</span>
              <span className="text-text-muted text-sm font-body">streak</span>
              {club && (
                <span className="ml-2 px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full font-body">
                  {club}
                </span>
              )}
            </div>
          </div>

          {/* Game Grid */}
          <GameGrid
            movieData={movieData}
            guesses={guesses}
            currentGuess={currentGuess}
            feedbacks={feedbacks}
            shakeRow={shakeRow}
            bounceRow={bounceRow}
            maxAttempts={MAX_ATTEMPTS}
          />

          {/* Hint Section */}
          <AnimatePresence>
            {canShowHintButton && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex justify-center mt-4"
              >
                <button
                  onClick={fetchHint}
                  className="flex items-center gap-2 px-4 py-2 bg-gold/20 hover:bg-gold/30 text-gold rounded-lg transition-colors font-body"
                  data-testid="hint-button"
                >
                  <Lightbulb className="w-4 h-4" />
                  <span>Need a hint?</span>
                </button>
              </motion.div>
            )}
            
            {showHint && hint && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-3 glass rounded-lg text-center"
              >
                <p className="text-gold text-sm font-body flex items-center justify-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  {hint}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Keyboard */}
      <div className="sticky bottom-0 bg-dark-paper/95 backdrop-blur-sm border-t border-white/5 py-3 px-2">
        <Keyboard
          onKeyPress={onKeyboardPress}
          keyStates={keyStates}
          disabled={gameStatus !== "playing"}
        />
      </div>

      {/* Modals */}
      <InstructionsModal
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        hardMode={hardMode}
        setHardMode={setHardMode}
        streak={streak}
        club={club}
      />

      <ResultModal
        isOpen={showResult}
        onClose={() => setShowResult(false)}
        gameStatus={gameStatus}
        guesses={guesses}
        feedbacks={feedbacks}
        revealData={revealData}
        movieData={movieData}
        streak={streak}
      />

      <ClubModal
        isOpen={showClub}
        onClose={() => {
          setShowClub(false);
          setShowResult(true);
        }}
        onSelect={handleClubSelect}
      />
    </div>
  );
};

export default GamePage;
